import { Mail, MessageCircle, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { getAnnouncementMailto, getAnnouncementSummary } from '../../_helpers';

import { useHubspotChat } from '@/app/hubspot/_hooks';
import { Button } from '@/app/ui/button';
import { cn } from '@/helpers/shadcn';

// One dot per announcement still waiting, the filled one being the tooltip on screen. It
// replaces the bare "2 more" the footer used to carry: the point of the count is "this is
// nearly over", and three dots say that faster than a sentence does.
const FeatureAnnouncementProgress = ({ remainingCount }) => (
  <div className="flex items-center gap-1.5" role="status" aria-label={`Update 1 of ${remainingCount}`}>
    {Array.from({ length: remainingCount }, (unused, index) => (
      <span key={index} className={cn('size-1.5 rounded-full', index === 0 ? 'bg-primary' : 'bg-primary/30')} />
    ))}
  </div>
);

// Every announcement ends in a way to ask about the feature, because the point of pointing at
// it is starting the conversation. That used to be a lone envelope icon, which says nothing
// about what is behind it — so it is now a block that makes the offer in words: support will
// walk you through the thing you have just been shown.
//
// The chat button is hidden rather than disabled while the widget is still loading: a
// permanently dead control inside a tooltip the user did not open reads as broken, which is
// the opposite of the reaction this is trying to get. Email is always there, so the block
// never loses its action. The help centre's Live Chat button stays instead, and waits for
// the embed on a press — it is the one place the user went looking for chat.
const FeatureAnnouncementSupport = ({ title }) => {
  const { isLoaded, openChat } = useHubspotChat();

  return (
    <div className="rounded-md border border-primary/25 bg-primary/5 p-2.5" data-testid="feature-announcement-support">
      <p className="text-sm font-medium">Want to see it in action?</p>
      <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">Support will walk you through {title} on one of your own projects, or answer anything about it.</p>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {isLoaded && (
          <Button variant="outline" size="sm" onClick={openChat} data-testid="feature-announcement-chat-button">
            <MessageCircle className="size-3.5" />
            Chat With Support
          </Button>
        )}
        <Button variant="outline" size="sm" asChild data-testid="feature-announcement-email-button">
          <a href={getAnnouncementMailto(title)}>
            <Mail className="size-3.5" />
            Email Support
          </a>
        </Button>
      </div>
    </div>
  );
};

// The body of one release tooltip.
const FeatureAnnouncementCard = ({ announcement, remainingCount, onDismiss, onClose }) => (
  <div className="space-y-3" data-testid="feature-announcement">
    <div className="flex items-start gap-2.5">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-primary to-primary/65 text-primary-foreground shadow-sm">
        <Sparkles className="size-4" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold tracking-wide text-primary uppercase">New in Zebel</p>
        <p className="text-base leading-tight font-semibold" data-testid="feature-announcement-title">
          {announcement.title}
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon-sm"
        className="-mt-1 -mr-1 shrink-0 text-muted-foreground"
        aria-label="Hide Feature Tips"
        onClick={onClose}
        data-testid="feature-announcement-close-button"
      >
        <X />
      </Button>
    </div>

    <div className="space-y-1">
      <p className="line-clamp-4 text-sm leading-relaxed text-muted-foreground" data-testid="feature-announcement-summary">
        {getAnnouncementSummary(announcement.description)}
      </p>
      <Link to="/product" className="text-sm font-medium text-primary hover:underline" data-testid="feature-announcement-read-more-link">
        Read More
      </Link>
    </div>

    <FeatureAnnouncementSupport title={announcement.title} />

    <div className="flex items-center justify-between gap-2 border-t pt-3">
      {remainingCount > 1 ? <FeatureAnnouncementProgress remainingCount={remainingCount} /> : <span />}
      <Button size="sm" onClick={onDismiss} data-testid="feature-announcement-dismiss-button">
        {remainingCount > 1 ? 'Next' : 'Got It'}
      </Button>
    </div>
  </div>
);

export default FeatureAnnouncementCard;
